import React, { useState, useRef, useEffect, useMemo } from 'react'
import { twMerge } from 'tailwind-merge'

interface Option {
  value: string | number;
  title: string;
  display?: React.ReactNode
}

interface SearchableSelectProps {
  id: string;
  label: string;
  options: Option[];
  value?: string | number;
  onChange: (value: any) => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  containerClassProps?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  id,
  label,
  options,
  value,
  onChange,
  error,
  disabled,
  placeholder,
  containerClassProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  const SubContainerStyle = 'px-2 py-1 bg-neutro-50 rounded-sm border relative'
  const SubContainerErrorState = error && !disabled ? "outline outline-erro-500" : ""
  const SubContainerFinalStyle = twMerge(SubContainerStyle, SubContainerErrorState)

  const LabelStyle = 'block text-primario-700 font-medium'
  const LabelFinalStyle = twMerge(
    LabelStyle, 
    disabled ? "text-neutro-200" : "text-primario-700",
    error && !disabled ? "text-erro-500" : ""
  )

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const selectedOptionTitle = useMemo(() => {
    const found = options.find(opt => String(opt.value) === String(value));
    return found ? found.title : "";
  }, [value, options]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(opt =>
      opt.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const handleSelectOption = (optionValue: any) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm(""); 
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) setSearchTerm("");
    }
  };

  return (
    <div className={containerClassProps} ref={wrapperRef}>
      <label className={LabelFinalStyle} htmlFor={id}>{label}</label>
      <div className={SubContainerFinalStyle}>
        <div className="relative">
          <input
            type="text"
            id={id}
            className={`w-full py-1 outline-none bg-transparent ${disabled ? 'text-neutro-200 cursor-not-allowed' : 'cursor-text'}`}
            placeholder={isOpen ? "Digite para buscar..." : (selectedOptionTitle || placeholder || "Selecione...")}
            value={isOpen ? searchTerm : selectedOptionTitle}
            onChange={handleInputChange}
            onClick={toggleDropdown}
            disabled={disabled}
            autoComplete="off"
          />
          
          <div className={`absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none transition-transform ${isOpen ? 'rotate-180' : ''}`}>
             <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L5 5L9 1" stroke={disabled ? "#CBD5E1" : "#334155"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
             </svg>
          </div>
        </div>

        {isOpen && !disabled && (
          <ul className="absolute left-0 right-0 top-[110%] bg-white border border-neutro-200 rounded-sm shadow-lg max-h-60 overflow-y-auto z-50">
             {filteredOptions.length > 0 ? (
               filteredOptions.map((option, index) => (
                 <li
                   key={index}
                   onClick={() => handleSelectOption(option.value)}
                   className={`px-3 py-2 cursor-pointer text-sm hover:bg-primario-50 hover:text-primario-700 transition-colors
                     ${String(value) === String(option.value) ? 'bg-primario-50 font-medium text-primario-700' : 'text-gray-700'}
                   `}
                 >
                   {option.display || option.title}
                 </li>
               ))
             ) : (
               <li className="px-3 py-2 text-gray-500 text-sm italic">Nenhum resultado encontrado.</li>
             )}
          </ul>
        )}
      </div>
      {error && !disabled && <p className='text-erro-500 mt-1'>{error}</p>}
    </div>
  );
}

export default SearchableSelect;